import React, { useState, useEffect } from "react";

// Redux
import { useSelector } from "react-redux";
import { selectUserData } from "../../../../redux/features/userState/userStateSlice";

// Custom components
import { ProfileStatsField } from "../../../../components/ValueFields/ValueFields";
import { Heading2 } from "../../../../components/Headings/Headings";
import DataTable from "../../../../components/DataTable/DataTable";

// API
import useAxios from "../../../../hooks/useAxios";
import jsonFormatAuth from "../../../../apis/jsonFormatAuth";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { SquareIconNavButton } from "../../../../components/Buttons/Buttons";
import HomeScreenGreeting from "../../../../components/HomeScreenGreeting/HomeScreenGreeting";

// Icons
import {
  UserGroupIcon,
  ClipboardDocumentListIcon,
} from "@heroicons/react/24/outline";

import { ReactComponent as PhotoIcon } from "./Icons/photo.svg";
import { ReactComponent as ComparisonIcon } from "./Icons/comparison.svg";
import { ReactComponent as PsoriasisCalculationIcon } from "./Icons/psoriasis_calculation.svg";
import { ReactComponent as PsoriasisSegmentationIcon } from "./Icons/psoriasis_segmentation.svg";
import { ReactComponent as BodySegmentationIcon } from "./Icons/body_segmentation.svg";

function HomeScreen() {
  const ENTRY_MODULES = JSON.parse(process.env.REACT_APP_ENTRY_MODULES || "[]");

  const userData = useSelector(selectUserData);
  const username = userData?.username || "";

  const { response, axiosFetch } = useAxios();

  // Table
  const [recentEntries, setRecentEntries] = useState([]);
  const [recentTags, setRecentTags] = useState([]);

  // Number of entries
  const [patientsCount, setPatientsCount] = useState(0);
  const [entriesCount, setEntriesCount] = useState(0);
  const [photoCount, setPhotoCount] = useState(0);
  const [comparisonCount, setComparisonCount] = useState(0);
  const [psoriasisCount, setPsoriasisEntriesCount] = useState(0);
  const [psoriasisSegmentationCount, setPsoriasisSegmentationCount] =
    useState(0);
  const [segmentationCount, setSegmentationCount] = useState(0);

  const [reRenderGreeting, setReRenderGreeting] = useState(false);

  const getData = () => {
    axiosFetch({
      axiosInstance: jsonFormatAuth(),
      method: "GET",
      url: "entries/home/",
      sendSignal: false,
      requestConfig: {},
    });
  };

  useEffect(() => {
    // Obtaining data from API on initial page load
    getData();
  }, []);

  // RESPONSE Detection
  useEffect(() => {
    // 1: Custom action code handling.
    // Patients data received.
    if (response) {
      if (response?.action_code === "home_data_sent") {
        if (response?.payload) {
          const profileStatsResponse = response.payload?.profile_stats;
          const recentEntriesResponse = response.payload?.recent_entries;
          const recentTagsResponse = response.payload?.recent_tags;

          if (profileStatsResponse) {
            const {
              patient_count,
              total_entry_count,
              photo_count,
              comparison_count,
              psoriasis_count,
              psoriasis_segmentation_count,
              segmentation_count,
            } = profileStatsResponse;

            setPatientsCount(patient_count || 0);
            setEntriesCount(total_entry_count || 0);
            setPhotoCount(photo_count || 0);
            setComparisonCount(comparison_count || 0);
            setPsoriasisEntriesCount(psoriasis_count || 0);
            setPsoriasisSegmentationCount(psoriasis_segmentation_count || 0);
            setSegmentationCount(segmentation_count || 0);
          }

          if (recentEntriesResponse) {
            setRecentEntries(recentEntriesResponse);
          } else {
            setRecentEntries([]);
          }

          if (recentTagsResponse) {
            setRecentTags(recentTagsResponse);
          } else {
            setRecentTags([]);
          }
        }
      }
    }
  }, [response]);

  // API End

  return (
    <div className="flex flex-col flex-grow items-center w-full pb-4 md:pb-0 Xbg-lime-500">
      <div className="flex flex-col-reverse md:flex-row items-center justify-between w-full Xbg-red-500">
        <div className="flex flex-[5] flex-row items-center justify-start w-full Xbg-green-500">
          {/* <Heading1 headingText={`Hello, ${username}!`} /> */}
          <HomeScreenGreeting
            username={username}
            reRenderGreeting={reRenderGreeting}
          />
        </div>
        <div className="flex flex-[1] flex-row items-center justify-end w-full h-full md:pt-5 pb-2 space-x-4 Xbg-yellow-500">
          <SquareIconNavButton
            Icon={ArrowPathIcon}
            actionFunction={() => {
              setReRenderGreeting(!reRenderGreeting);
              getData();
            }}
            activeColor={"active:bg-cyan-100"}
          />
        </div>
      </div>

      <div className="py-4" />

      <Heading2 headingText="Profile Statistics" />

      <div className="py-2" />

      <div className="flex flex-col md:flex-row py-2 space-y-4 md:space-y-0 md:space-x-4 w-full Xbg-orange-200">
        <ProfileStatsField
          name={"Patients"}
          value={patientsCount}
          Icon={UserGroupIcon}
          iconType={"hero"}
        />
        <ProfileStatsField
          name={"Entries"}
          value={entriesCount}
          Icon={ClipboardDocumentListIcon}
          iconType={"hero"}
        />
      </div>

      <div className="flex flex-col xl:flex-row py-2 space-y-4 xl:space-y-0 xl:space-x-4 w-full Xbg-orange-200">
        {ENTRY_MODULES.includes("photo") && (
          <>
            <ProfileStatsField
              name={"Photo Entries"}
              value={photoCount}
              Icon={PhotoIcon}
              iconType={"google"}
            />
            <ProfileStatsField
              name={"Comparison Entries"}
              value={comparisonCount}
              Icon={ComparisonIcon}
              iconType={"google"}
            />
          </>
        )}
      </div>

      <div className="flex flex-col py-2 w-full Xbg-orange-200">
        {ENTRY_MODULES.includes("psoriasis") && (
          <ProfileStatsField
            name={"Psoriasis Entries"}
            value={psoriasisCount}
            Icon={PsoriasisCalculationIcon}
            iconType={"google"}
          />
        )}
      </div>

      <div className="flex flex-col xl:flex-row py-2 space-y-4 xl:space-y-0 xl:space-x-4 w-full Xbg-orange-200">
        {ENTRY_MODULES.includes("psoriasis") && (
          <ProfileStatsField
            name={"PASI Calculations"}
            value={psoriasisSegmentationCount}
            Icon={PsoriasisSegmentationIcon}
            iconType={"google"}
          />
        )}
        {ENTRY_MODULES.includes("ai_assisted") && (
          <ProfileStatsField
            name={"Body Segmentation"}
            value={segmentationCount}
            Icon={BodySegmentationIcon}
            iconType={"google"}
          />
        )}
      </div>

      <div className="py-4" />

      <Heading2 headingText={"Recent Entries"} />

      <div className="py-2" />

      <DataTable
        data={recentEntries}
        dataTags={recentTags}
        rowType={"home"}
        showFilter={true}
        showPageNav={false}
      />
    </div>
  );
}

export default HomeScreen;
